/**
 * Cookie consent configuration builders per jurisdiction.
 *
 * Each builder returns a `vanilla-cookieconsent` v3 config object tailored to
 * the jurisdiction's regulatory regime:
 *
 *   - APP   (AU)  — Privacy Act 1988; notify + opt-out acceptable for analytics
 *   - PDPA  (SG)  — Personal Data Protection Act 2012; explicit opt-in required
 *                   for non-functional cookies; banner cannot pre-tick categories
 *   - PDPL  (ID)  — UU PDP; granular explicit opt-in per category (analytics,
 *                   marketing, functional); no implicit consent paths
 *   - FJ-PA (FJ)  — Privacy Act 2025; similar to APP — notify + opt-out for analytics
 *
 * Authority: ADR-0037 (vanilla-cookieconsent v3 carveout); code-handoff-fy27
 * § 12.3 (RegionConfig.compliance) and § 12.8 (cookie consent direction).
 *
 * The functions return plain config — no DOM/runtime side effects — so they
 * compose cleanly with SSR (vite-react-ssg) and unit tests without import-time
 * CookieConsent activation.
 */

import type { CookieConsentConfig } from 'vanilla-cookieconsent';
import type { RegionConfig } from '../regions/regionConfig';

/**
 * Common privacy / cookie policy URLs by region — link target for the
 * "Read our privacy policy" element in the consent UI.
 *
 * All four regions point at the same in-app `/privacy` route today; per-region
 * privacy pages may diverge as Stephen + partners supply jurisdiction-specific
 * content (OQ-11/12 in code-handoff brief).
 */
function privacyHref(region: RegionConfig): string {
  return `https://${region.host}/privacy`;
}

/**
 * APP — Australian Privacy Principles (AU).
 *
 * Notify of personal information handling; opt-out model is acceptable for
 * analytics-style cookies. The banner offers "Accept all" + "Reject all" + a
 * preferences modal for granularity. Categories pre-set to enabled=false so
 * the user explicitly enables (more conservative than APP minimum; aligns
 * with how Conscierra describes its privacy posture publicly).
 */
function buildAuConfig(region: RegionConfig): CookieConsentConfig {
  return {
    revision: 1,
    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom right',
        equalWeightButtons: true,
        flipButtons: false,
      },
      preferencesModal: {
        layout: 'box',
        equalWeightButtons: true,
        flipButtons: false,
      },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: { enabled: false, readOnly: false },
      marketing: { enabled: false, readOnly: false },
    },
    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We use cookies',
            description:
              'Conscierra uses essential cookies to make this site work, and (with your permission) analytics cookies to understand how visitors use the site so we can improve it. You can change your choice at any time from the footer.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Manage preferences',
            footer: `<a href="${privacyHref(region)}">Privacy policy</a>`,
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'How we use cookies',
                description:
                  'Conscierra operates under the Australian Privacy Principles (APP). Essential cookies are always on — without them the site does not work. You can choose whether to enable analytics. Marketing cookies are only used when you explicitly accept them.',
              },
              {
                title: 'Strictly necessary',
                description:
                  'Required for the site to function — authentication state, session continuity, and your cookie-consent decision itself.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics',
                description:
                  'Aggregate usage metrics via Microsoft Application Insights — page views, performance timings, error counts. Helps us improve the site. No personal identifiers are sent.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing',
                description:
                  'Tracking pixels and re-targeting cookies used when we run paid campaigns. Off by default.',
                linkedCategory: 'marketing',
              },
              {
                title: 'More information',
                description: `For questions about our cookie use, see our <a href="${privacyHref(region)}">privacy policy</a> or contact ${region.email}.`,
              },
            ],
          },
        },
      },
    },
  };
}

/**
 * PDPA — Personal Data Protection Act (SG).
 *
 * Explicit opt-in required for non-functional cookies. The banner cannot
 * default analytics or marketing to on; "Accept all" is the affirmative
 * action. Reject path keeps only `necessary` enabled.
 */
function buildSgConfig(region: RegionConfig): CookieConsentConfig {
  return {
    revision: 1,
    guiOptions: {
      consentModal: { layout: 'box inline', position: 'bottom right', equalWeightButtons: true },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: { enabled: false, readOnly: false },
      marketing: { enabled: false, readOnly: false },
    },
    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'Your cookie choices',
            description:
              'Under Singapore’s Personal Data Protection Act (PDPA), we ask for your explicit consent before setting non-essential cookies. Essential cookies are required for the site to work; analytics and marketing cookies are off until you turn them on.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Manage preferences',
            footer: `<a href="${privacyHref(region)}">Privacy policy</a>`,
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'How we use cookies in Singapore',
                description:
                  'PDPA requires explicit consent for any cookie that is not strictly necessary. We do not set analytics or marketing cookies unless you opt in.',
              },
              {
                title: 'Strictly necessary',
                description:
                  'Required for the site to function — authentication state, session continuity, and your cookie-consent decision itself.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics',
                description:
                  'Aggregate usage metrics via Microsoft Application Insights. Off until you opt in.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing',
                description:
                  'Tracking and re-targeting cookies used during paid campaigns. Off until you opt in.',
                linkedCategory: 'marketing',
              },
              {
                title: 'More information',
                description: `Questions: see our <a href="${privacyHref(region)}">privacy policy</a> or email ${region.email}.`,
              },
            ],
          },
        },
      },
    },
  };
}

/**
 * PDPL — UU PDP (ID).
 *
 * Granular explicit opt-in required across every non-essential category. We
 * split `functional` out from `analytics`/`marketing` so the user can choose
 * each independently. No implicit consent path.
 */
function buildIdConfig(region: RegionConfig): CookieConsentConfig {
  return {
    revision: 1,
    guiOptions: {
      consentModal: { layout: 'box inline', position: 'bottom right', equalWeightButtons: true },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      functional: { enabled: false, readOnly: false },
      analytics: { enabled: false, readOnly: false },
      marketing: { enabled: false, readOnly: false },
    },
    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'Cookie consent',
            description:
              'Indonesia’s Personal Data Protection Law (UU PDP) requires explicit consent for any cookie beyond the strictly necessary. Choose each category yourself — every non-essential category is off until you turn it on.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Choose each category',
            footer: `<a href="${privacyHref(region)}">Privacy policy</a>`,
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'How we use cookies in Indonesia',
                description:
                  'UU PDP requires granular consent per cookie category. We honour that — every non-essential category below has its own switch and is off by default.',
              },
              {
                title: 'Strictly necessary',
                description:
                  'Required for the site to function — authentication state, session continuity, and your cookie-consent decision itself.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Functional',
                description:
                  'Remembers preferences such as your language choice (English / Bahasa Indonesia). Off until you opt in.',
                linkedCategory: 'functional',
              },
              {
                title: 'Analytics',
                description:
                  'Aggregate usage metrics via Microsoft Application Insights. Off until you opt in.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing',
                description:
                  'Tracking and re-targeting cookies used during paid campaigns. Off until you opt in.',
                linkedCategory: 'marketing',
              },
              {
                title: 'More information',
                description: `Questions: see our <a href="${privacyHref(region)}">privacy policy</a> or email ${region.email}.`,
              },
            ],
          },
        },
      },
    },
  };
}

/**
 * FJ-PA — Privacy Act 2025 (FJ). Notify + opt-out for analytics acceptable;
 * banner shape mirrors AU.
 */
function buildFjConfig(region: RegionConfig): CookieConsentConfig {
  return {
    revision: 1,
    guiOptions: {
      consentModal: { layout: 'box inline', position: 'bottom right', equalWeightButtons: true },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: { enabled: false, readOnly: false },
      marketing: { enabled: false, readOnly: false },
    },
    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'We use cookies',
            description:
              'Conscierra uses essential cookies to make this site work, and (with your permission) analytics cookies to understand how visitors use the site so we can improve it. You can change your choice at any time from the footer.',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            showPreferencesBtn: 'Manage preferences',
            footer: `<a href="${privacyHref(region)}">Privacy policy</a>`,
          },
          preferencesModal: {
            title: 'Cookie preferences',
            acceptAllBtn: 'Accept all',
            acceptNecessaryBtn: 'Reject all',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close',
            sections: [
              {
                title: 'How we use cookies',
                description:
                  'Conscierra operates under the Fijian Privacy Act 2025. Essential cookies are always on; analytics and marketing cookies are off unless you opt in.',
              },
              {
                title: 'Strictly necessary',
                description:
                  'Required for the site to function — authentication state, session continuity, and your cookie-consent decision itself.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Analytics',
                description:
                  'Aggregate usage metrics via Microsoft Application Insights. No personal identifiers are sent.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Marketing',
                description:
                  'Tracking pixels and re-targeting cookies used when we run paid campaigns. Off by default.',
                linkedCategory: 'marketing',
              },
              {
                title: 'More information',
                description: `Questions: see our <a href="${privacyHref(region)}">privacy policy</a> or email ${region.email}.`,
              },
            ],
          },
        },
      },
    },
  };
}

/**
 * Dispatch to the correct jurisdiction config builder based on the active
 * region's `privacyJurisdiction` discriminator.
 */
export function buildCookieConsentConfig(region: RegionConfig): CookieConsentConfig {
  switch (region.privacyJurisdiction) {
    case 'APP':
      return buildAuConfig(region);
    case 'PDPA':
      return buildSgConfig(region);
    case 'PDPL':
      return buildIdConfig(region);
    case 'FJ-PA':
      return buildFjConfig(region);
  }
}
